import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { StoreState } from "../../redux/reducers";
import {
  fetchLeaveRequests,
  addLeaveRequest,
  editLeaveRequest,
  deleteLeaveRequestById,
  editLeaveStatus,
} from "../../redux/actions/leaveRequestAction";
import Main from "../../components/custom/Main";
import PageContainer from "../../components/custom/PageContainer";
import Modal from "../../components/Modal";
import CollapseCard from "../../components/custom/CollapseCard";
import { LeaveRequest } from "../../types/LeaveRequest";
import LeaveRequestForm from "../../components/LeaveRequestForm";
import LeaveRequestList from "../../components/LeaveRequestList";
import { ToastTypes, useToast } from "../../components/toast/ToastProvider";
import { AuthUserType } from "../../redux/actions/authUserActions";
import {
  fetchLeavesAction,
  fetchLeavesBalanceAction,
} from "../../redux/actions/leaveTransactionAction";
import "../../styles/Attendance.css";

const initialLeaveRequests: LeaveRequest[] = [];

const EmployeeLeaveRequestPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const leaveRequests = useSelector((state: StoreState) => state.leaveRequest);
  const authUser = useSelector<StoreState, AuthUserType>(
    (state) => state.authUser,
  );
  const leaveBalance = useSelector(
    (state: StoreState) => state.leaveTransaction,
  );

  const [leaveRequest, setLeaveRequest] =
    useState<LeaveRequest[]>(initialLeaveRequests);
  const [selected, setSelected] = useState<LeaveRequest | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const leaveTypes = ["el", "pl"];

  useEffect(() => {
    setLoading(true);
    dispatch(fetchLeaveRequests()).finally(() => setLoading(false));
  }, [dispatch]);
  useEffect(() => {
    if (authUser?.employeeId) {
      dispatch(fetchLeavesBalanceAction(authUser.employeeId));
    }
  }, [authUser?.employeeId, dispatch]);
  const handleSave = async (req: LeaveRequest) => {
    setLoading(true);
    try {
      const balanceItem = leaveBalance.find(
        (lb) => lb.leaveType === req.leaveType,
      );
      const balance = balanceItem?.balance ?? 0;

      // Prevent applying if balance is zero and it's a new request
      if (!selected && balance <= 0) {
        toast("Insufficient leave balance. Cannot apply for this leave.");
        setLoading(false);
        return;
      }

      if (selected) {
        await dispatch(editLeaveRequest(req.id, req));

        toast("Leave request updated successfully");
      } else {
        await dispatch(addLeaveRequest(req));

        toast("Leave request added successfully");
      }
      await dispatch(fetchLeaveRequests());
      setSelected(null);
      setShowModal(false);
    } catch (error) {
      toast("Error saving leave request");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this leave request?")) {
      setLoading(true);
      try {
        await dispatch(deleteLeaveRequestById(id));
        toast("Leave request deleted successfully");
        await dispatch(fetchLeaveRequests());
      } catch (error) {
        toast("Error deleting leave request");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (item: LeaveRequest) => {
    setSelected(item);
    setShowModal(true);
  };

  const handleApprove = async (req: LeaveRequest) => {
    if (req.status === "Approved") return;

    const updated = {
      ...req,
      status: "Approved",
      approvedBy: authUser?.name || "Unknown", // use name from auth context
    };

    setLoading(true);
    try {
      await dispatch(editLeaveStatus(req.id, updated));
      toast("Leave request approved successfully");
      await dispatch(fetchLeaveRequests());
      await dispatch(fetchLeavesAction());
    } catch (error) {
      toast("Error approving leave request");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (req: LeaveRequest) => {
    const updated = {
      ...req,
      status: "Cancelled",
      approvedBy: authUser?.name || "Unknown",
    };

    setLoading(true);
    try {
      await dispatch(editLeaveStatus(req.id, updated));
      toast("Leave request approved successfully");
      await dispatch(fetchLeaveRequests());
      await dispatch(fetchLeavesAction());
    } catch (error) {
      toast("Error approving leave request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Main>
      <PageContainer
        title="Leave Request Management"
        buttonText={loading ? "Loading..." : "ADD"}
        onButtonClick={() => setShowModal(true)}
      >
        <CollapseCard title="Leave Balance">
          <div className="container">
            <div className="attendance-card-row mt-4 mb-3 ">
              {[
                {
                  key: "credit",
                  label: "Credit",
                  className: "first-box",
                },
                {
                  key: "debit",
                  label: "Debit",
                  color: "bg-danger text-white",
                  className: "second-box",
                },
                {
                  key: "balance",
                  label: "Balance",
                  className: "third-box",
                },
                {
                  key: "pending",
                  label: "Pending",
                  className: "fourth-box",
                },
              ].map(({ key, label, className }) => (
                <div key={key} className={`${className}`}>
                  <div className="attendance-card">
                    <h5 className="text-capitalize mb-3 text-center">
                      {label}
                    </h5>
                    {leaveTypes.map((type) => {
                      const leave = leaveBalance.find(
                        (lb) => lb.leaveType === type,
                      );
                      const value =
                        key === "credit"
                          ? leave?.credit
                          : key === "debit"
                            ? leave?.debit
                            : key === "balance"
                              ? leave?.balance
                              : (leave?.pending ?? 0);

                      return (
                        <div
                          key={type}
                          className="d-flex justify-content-center"
                        >
                          <span className="text-uppercase">{type}:</span>
                          <span>{value ?? 0}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CollapseCard>
        <Modal
          visible={showModal}
          size="lg"
          onClose={() => {
            setShowModal(false);
            setSelected(null);
          }}
          headerText={selected ? "Update Leave Request" : "Add Leave Request"}
        >
          <LeaveRequestForm
            onSave={handleSave}
            initialData={selected}
            clearAfterSubmit={() => setSelected(null)}
            employeeId={authUser.employeeId || ""}
          />
        </Modal>

        <CollapseCard title="Leave Requests">
          <LeaveRequestList
            leaveRequests={leaveRequests}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onApprove={handleApprove}
            onCancel={handleCancel}
          />
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default EmployeeLeaveRequestPage;
